import { useState } from "react";
import { Link } from "react-router-dom";
import { SERVICES, SITE } from "../lib/site";
import ServiceIcon from "../components/ServiceIcon";
import { Check, Camera } from "lucide-react";
import usePageMeta from "../hooks/usePageMeta";

function PriceCalculator({ withRecordingByDefault = false, mandatoryRecording = false }) {
  const [hours, setHours] = useState(1);
  const [recording, setRecording] = useState(withRecordingByDefault || mandatoryRecording);

  const session = SITE.pricing.inPerson.firstHour + Math.max(0, hours - 1) * SITE.pricing.inPerson.additional;
  const rec = recording ? SITE.pricing.recordingAddon.firstHour + Math.max(0, hours - 1) * SITE.pricing.recordingAddon.additional : 0;
  const total = session + rec;

  return (
    <div className="border border-[#c9a04b]/25 bg-charcoalMuted p-6 md:p-8 mt-6" data-testid="price-calculator">
      <div className="eyebrow mb-5">Estimate</div>

      <div className="flex flex-col sm:flex-row sm:items-center gap-5 sm:gap-10 mb-6">
        <div className="flex-1">
          <label className="text-xs uppercase tracking-[0.2em] text-cream/60 block mb-3">Duration (hours)</label>
          <div className="flex items-center gap-3">
            <button
              type="button"
              aria-label="Decrease hours"
              onClick={() => setHours(h => Math.max(1, h - 1))}
              data-testid="calc-decrease"
              className="w-10 h-10 border border-[#c9a04b]/35 text-cream hover:border-gold transition-colors"
            >−</button>
            <input
              type="number"
              min={1}
              max={12}
              value={hours}
              onChange={(e) => setHours(Math.max(1, Math.min(12, Number(e.target.value) || 1)))}
              data-testid="calc-hours"
              className="w-20 text-center bg-transparent border border-[#c9a04b]/35 py-2.5 text-cream font-serif text-lg"
            />
            <button
              type="button"
              aria-label="Increase hours"
              onClick={() => setHours(h => Math.min(12, h + 1))}
              data-testid="calc-increase"
              className="w-10 h-10 border border-[#c9a04b]/35 text-cream hover:border-gold transition-colors"
            >+</button>
          </div>
        </div>

        <div className="flex-1">
          <label className="text-xs uppercase tracking-[0.2em] text-cream/60 block mb-3">Recording</label>
          <button
            type="button"
            disabled={mandatoryRecording}
            onClick={() => !mandatoryRecording && setRecording(r => !r)}
            data-testid="calc-recording-toggle"
            className={`w-full flex items-center justify-between px-4 py-3 border transition-colors ${
              recording ? "border-gold bg-gold/10" : "border-[#c9a04b]/35 hover:border-gold"
            } ${mandatoryRecording ? "opacity-80 cursor-not-allowed" : ""}`}
          >
            <span className="text-cream text-sm flex items-center gap-2">
              <Camera size={14} className="text-gold" />
              Include fly-on-the-wall recording
            </span>
            <span className={`w-4 h-4 border ${recording ? "border-gold bg-gold" : "border-[#c9a04b]/50"}`} />
          </button>
        </div>
      </div>

      <div className="border-t border-[#c9a04b]/15 pt-5 grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
        <div>
          <div className="text-cream/55 text-[11px] uppercase tracking-[0.2em] mb-1">Session</div>
          <div className="text-cream">£{session.toLocaleString()}</div>
        </div>
        <div>
          <div className="text-cream/55 text-[11px] uppercase tracking-[0.2em] mb-1">Recording add-on</div>
          <div className="text-cream">£{rec.toLocaleString()}</div>
        </div>
        <div>
          <div className="text-gold text-[11px] uppercase tracking-[0.2em] mb-1">Estimated total</div>
          <div className="font-serif text-3xl text-cream">£{total.toLocaleString()}</div>
        </div>
      </div>

      <p className="mt-4 text-[11px] text-cream/45 tracking-wider">Indicative pricing in GBP. Final amount confirmed by Cleopatra on booking.</p>
    </div>
  );
}

export default function Services() {
  usePageMeta({title:"Erotic Hypnosis Services & Rates",description:"Six tiers of erotic hypnosis in London and via webcam. In-person, extended, bespoke programmes, and custom fly-on-the-wall video sessions. Rates from £150.",path:"/services"});
  return (
    <div data-testid="page-services">
      <section className="py-24 md:py-32 max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid md:grid-cols-12 gap-10 md:gap-16 items-end">
          <div className="md:col-span-7">
            <div className="eyebrow mb-5">The Practice</div>
            <h1 className="font-serif text-5xl md:text-7xl text-cream leading-[1] max-w-3xl">
              Sessions tailored to <em className="text-gold">desire</em> and safety.
            </h1>
            <p className="mt-8 max-w-2xl text-cream/70 leading-relaxed">
              Specialist in-person sessions and consensual recorded sessions are the core offering.
              Remote modalities - webcam, phone, text - extend the reach of the practice while
              preserving its intimacy.
            </p>
          </div>
          <div className="md:col-span-5">
            <figure className="relative max-w-sm mx-auto md:ml-auto md:mr-0">
              <div className="absolute inset-0 -translate-x-3 -translate-y-3 border border-[#c9a04b]/40 pointer-events-none" />
              <div className="relative aspect-square overflow-hidden border border-[#c9a04b]/30 bg-charcoalMuted">
                <img
                  src="https://customer-assets.emergentagent.com/job_trance-bookings/artifacts/9y6uucx7_Cleo%20erotic%20hypnosis%20pocket%20watch.jpg"
                  alt="Gold pocket watch suspended over black lace - the pendulum at rest"
                  className="w-full h-full object-cover"
                />
              </div>
              <figcaption className="mt-3 eyebrow text-cream/55 text-right">The Pendulum</figcaption>
            </figure>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 pb-32 space-y-16 md:space-y-24">
        {SERVICES.map((s) => (
          <article
            key={s.slug}
            id={s.slug}
            data-testid={`service-${s.slug}`}
            className="grid md:grid-cols-12 gap-8 md:gap-12 border-t border-[#c9a04b]/15 pt-12 md:pt-16"
          >
            <div className="md:col-span-1">
              <ServiceIcon name={s.icon} size={36} />
            </div>
            <div className="md:col-span-5">
              <div className="eyebrow mb-3">Service</div>
              <h2 className="font-serif text-3xl md:text-5xl text-cream leading-tight mb-4">{s.title}</h2>
              <p className="text-cream/70 leading-relaxed mb-6">{s.blurb}</p>
              <div className="text-gold text-sm tracking-wide mb-6">{s.pricing}</div>
              <Link to="/book" className="btn-primary inline-flex" data-testid={`service-cta-${s.slug}`}>{s.cta}</Link>
            </div>
            <div className="md:col-span-6">
              {s.slug === "fly-on-the-wall" && (
                <figure className="mb-6 relative">
                  <div className="absolute inset-0 -translate-x-2 -translate-y-2 border border-[#c9a04b]/30 pointer-events-none" />
                  <div className="relative aspect-[16/10] overflow-hidden border border-[#c9a04b]/30 bg-charcoal">
                    <img
                      src="https://customer-assets.emergentagent.com/job_trance-bookings/artifacts/jd8gr8d7_Dolls.jpg"
                      alt="Dolls arranged before a hypnotic spiral - emblem of the practice"
                      className="w-full h-full object-cover"
                    />
                    <div
                      aria-hidden="true"
                      className="absolute inset-0 pointer-events-none"
                      style={{
                        background:
                          "linear-gradient(180deg, rgba(17,19,25,0) 50%, rgba(17,19,25,0.6) 100%)",
                      }}
                    />
                  </div>
                  <figcaption className="mt-3 eyebrow text-cream/55">The Spiral · An emblem of the practice</figcaption>
                </figure>
              )}
              {s.calculator ? (
                <PriceCalculator
                  withRecordingByDefault={s.slug === "fly-on-the-wall"}
                  mandatoryRecording={!!s.recordingMandatory}
                />
              ) : (
                <ul className="space-y-3 text-sm text-cream/70 pt-6">
                  <li className="flex gap-3"><Check size={14} className="text-gold mt-1 shrink-0" /> Discreet bookings and aftercare</li>
                  <li className="flex gap-3"><Check size={14} className="text-gold mt-1 shrink-0" /> Explicit, written consent at every step</li>
                  <li className="flex gap-3"><Check size={14} className="text-gold mt-1 shrink-0" /> Tailored to your boundaries and goals</li>
                  <li className="flex gap-3"><Check size={14} className="text-gold mt-1 shrink-0" /> Safewords and structured re-orientation</li>
                </ul>
              )}
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}
